<template>
  <UmisisModal
    ref="scheduleModal"
    title="Edit Schedule"
    :ok-disabled="isSaving || !canSubmit"
    :ok-title="isSaving ? 'Saving...' : 'Save Schedule'"
    @show="onModalShow"
    @ok="submitSchedule"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="schedule-starts-at">Starts At</FieldLabel>
        <Input
          id="schedule-starts-at"
          v-model="startsAt"
          type="datetime-local"
        />
      </Field>

      <Field>
        <FieldLabel for="schedule-frequency">Frequency</FieldLabel>
        <select
          id="schedule-frequency"
          v-model="frequency"
          class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="MONTHLY">Monthly</option>
          <option value="YEARLY">Yearly</option>
        </select>
      </Field>

      <Field>
        <FieldLabel for="schedule-interval">Interval</FieldLabel>
        <Input
          id="schedule-interval"
          v-model="intervalInput"
          type="number"
          min="1"
        />
        <FieldDescription>
          Repeat every {{ intervalUnitLabel }}.
        </FieldDescription>
      </Field>

      <Field v-if="frequency === 'WEEKLY'">
        <FieldLabel>Days of week</FieldLabel>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label
            v-for="day in WEEKDAY_OPTIONS"
            :key="day.value"
            class="flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
          >
            <input
              type="checkbox"
              :value="day.value"
              :checked="selectedWeekdays.includes(day.value)"
              @change="toggleWeekday(day.value)"
            />
            <span>{{ day.label }}</span>
          </label>
        </div>
      </Field>

      <Field>
        <FieldLabel for="schedule-end-type">Ends</FieldLabel>
        <select
          id="schedule-end-type"
          v-model="endType"
          class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="never">Never</option>
          <option value="count">After number of occurrences</option>
          <option value="until">On date</option>
        </select>
      </Field>

      <Field v-if="endType === 'count'">
        <FieldLabel for="schedule-end-count">Occurrences</FieldLabel>
        <Input
          id="schedule-end-count"
          v-model="endCountInput"
          type="number"
          min="1"
        />
      </Field>

      <Field v-if="endType === 'until'">
        <FieldLabel for="schedule-end-until">Until date</FieldLabel>
        <Input
          id="schedule-end-until"
          v-model="endUntilDate"
          type="date"
        />
      </Field>

      <p class="text-sm text-muted-foreground">
        {{ recurrencePreview }}
      </p>
    </div>

    <template #footer="{ cancel }">
      <Button type="button" variant="outline" :disabled="isSaving" @click="cancel">
        Cancel
      </Button>
      <Button
        type="button"
        :disabled="isSaving || !canSubmit"
        @click="submitSchedule"
      >
        {{ isSaving ? 'Saving...' : 'Save Schedule' }}
      </Button>
    </template>
  </UmisisModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import { useScheduleHumanReadable } from '../../composables/useScheduleHumanReadable.js'

const WEEKDAY_OPTIONS = [
  { value: 'MO', label: 'Monday' },
  { value: 'TU', label: 'Tuesday' },
  { value: 'WE', label: 'Wednesday' },
  { value: 'TH', label: 'Thursday' },
  { value: 'FR', label: 'Friday' },
  { value: 'SA', label: 'Saturday' },
  { value: 'SU', label: 'Sunday' },
]

const WEEKDAY_ORDER = WEEKDAY_OPTIONS.reduce((accumulator, option, index) => {
  accumulator[option.value] = index
  return accumulator
}, {})

const STARTS_AT_DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
const ALLOWED_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

const props = defineProps({
  schedule: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  saveSchedule: {
    type: Function,
    default: null,
  },
})

const startsAt = ref('')
const frequency = ref('DAILY')
const intervalInput = ref('1')
const selectedWeekdays = ref([])
const endType = ref('never')
const endCountInput = ref('1')
const endUntilDate = ref('')

const { componentRef: scheduleModal, openModal, closeModal } = useModalControl()
const { toHumanReadableRrule } = useScheduleHumanReadable()

const normalizedInterval = computed(() => {
  const parsedInterval = Number(intervalInput.value)

  if (!Number.isInteger(parsedInterval) || parsedInterval < 1) {
    return null
  }

  return parsedInterval
})

const normalizedEndCount = computed(() => {
  const parsedCount = Number(endCountInput.value)

  if (!Number.isInteger(parsedCount) || parsedCount < 1) {
    return null
  }

  return parsedCount
})

const hasValidStartsAt = computed(() => {
  if (startsAt.value.trim() === '') {
    return false
  }

  const parsedDate = new Date(startsAt.value)

  return !Number.isNaN(parsedDate.getTime())
})

const hasValidWeeklySelection = computed(() => {
  if (frequency.value !== 'WEEKLY') {
    return true
  }

  return selectedWeekdays.value.length > 0
})

const hasValidEndCondition = computed(() => {
  if (endType.value === 'never') {
    return true
  }

  if (endType.value === 'count') {
    return normalizedEndCount.value !== null
  }

  if (endType.value === 'until') {
    return isValidDateInput(endUntilDate.value)
  }

  return false
})

const canSubmit = computed(() => {
  return hasValidStartsAt.value
    && normalizedInterval.value !== null
    && hasValidWeeklySelection.value
    && hasValidEndCondition.value
    && typeof props.saveSchedule === 'function'
})

const intervalUnitLabel = computed(() => {
  const interval = normalizedInterval.value ?? 1

  if (frequency.value === 'WEEKLY') {
    return interval === 1 ? 'week' : 'weeks'
  }

  if (frequency.value === 'MONTHLY') {
    return interval === 1 ? 'month' : 'months'
  }

  if (frequency.value === 'YEARLY') {
    return interval === 1 ? 'year' : 'years'
  }

  return interval === 1 ? 'day' : 'days'
})

const rrule = computed(() => {
  return buildRruleFromState()
})

const recurrencePreview = computed(() => {
  return toHumanReadableRrule(rrule.value)
})

watch(frequency, (value) => {
  if (value === 'WEEKLY' && selectedWeekdays.value.length === 0) {
    selectedWeekdays.value = [resolveDayCodeFromStartsAt()]
  }
})

const toDateTimeLocalValue = (value) => {
  if (!value) {
    return ''
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')
  const hours = String(parsedDate.getHours()).padStart(2, '0')
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const isValidDateInput = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
}

const parseRruleParts = (rawRrule) => {
  if (typeof rawRrule !== 'string' || rawRrule.trim() === '') {
    return {}
  }

  const parts = {}

  for (const chunk of rawRrule.split(';')) {
    const [rawKey, ...valueParts] = chunk.split('=')
    const key = rawKey?.trim()?.toUpperCase()
    const value = valueParts.join('=').trim()

    if (!key || value === '') {
      continue
    }

    parts[key] = value
  }

  return parts
}

const parseWeeklyDays = (rawByDay) => {
  if (typeof rawByDay !== 'string' || rawByDay.trim() === '') {
    return []
  }

  const validDayCodes = new Set(WEEKDAY_OPTIONS.map((option) => option.value))

  return rawByDay
    .split(',')
    .map((value) => value.trim().toUpperCase())
    .filter((value) => validDayCodes.has(value))
    .sort((first, second) => (WEEKDAY_ORDER[first] ?? 0) - (WEEKDAY_ORDER[second] ?? 0))
}

const parseUntilDateToInput = (rawUntil) => {
  if (typeof rawUntil !== 'string' || rawUntil.trim() === '') {
    return ''
  }

  const compactMatch = rawUntil.trim().match(/^(\d{4})(\d{2})(\d{2})/)

  if (compactMatch) {
    const [, year, month, day] = compactMatch
    return `${year}-${month}-${day}`
  }

  const parsedDate = new Date(rawUntil)

  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const year = parsedDate.getUTCFullYear()
  const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getUTCDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const resolveDayCodeFromStartsAt = () => {
  const parsedStartsAt = new Date(startsAt.value)

  if (Number.isNaN(parsedStartsAt.getTime())) {
    return 'MO'
  }

  return STARTS_AT_DAY_CODES[parsedStartsAt.getDay()] ?? 'MO'
}

const onModalShow = () => {
  startsAt.value = toDateTimeLocalValue(props.schedule?.starts_at)
  frequency.value = 'DAILY'
  intervalInput.value = '1'
  selectedWeekdays.value = []
  endType.value = 'never'
  endCountInput.value = '1'
  endUntilDate.value = ''

  const parts = parseRruleParts(props.schedule?.rrule)
  const resolvedFrequency = String(parts.FREQ ?? 'DAILY').toUpperCase()

  if (ALLOWED_FREQUENCIES.includes(resolvedFrequency)) {
    frequency.value = resolvedFrequency
  }

  const resolvedInterval = Number(parts.INTERVAL)

  if (Number.isInteger(resolvedInterval) && resolvedInterval > 0) {
    intervalInput.value = String(resolvedInterval)
  }

  if (frequency.value === 'WEEKLY') {
    const parsedByDay = parseWeeklyDays(parts.BYDAY)
    selectedWeekdays.value = parsedByDay.length > 0 ? parsedByDay : [resolveDayCodeFromStartsAt()]
  }

  const resolvedCount = Number(parts.COUNT)

  if (Number.isInteger(resolvedCount) && resolvedCount > 0) {
    endType.value = 'count'
    endCountInput.value = String(resolvedCount)
  } else if (typeof parts.UNTIL === 'string' && parts.UNTIL.trim() !== '') {
    const parsedUntilDate = parseUntilDateToInput(parts.UNTIL)

    if (parsedUntilDate !== '') {
      endType.value = 'until'
      endUntilDate.value = parsedUntilDate
    }
  }

  if (startsAt.value === '') {
    startsAt.value = toDateTimeLocalValue(new Date())
  }
}

const toggleWeekday = (dayCode) => {
  const values = new Set(selectedWeekdays.value)

  if (values.has(dayCode)) {
    values.delete(dayCode)
  } else {
    values.add(dayCode)
  }

  selectedWeekdays.value = Array.from(values).sort(
    (first, second) => (WEEKDAY_ORDER[first] ?? 0) - (WEEKDAY_ORDER[second] ?? 0)
  )
}

const formatUntilValue = (dateInput) => {
  if (!isValidDateInput(dateInput)) {
    return null
  }

  const compact = dateInput.replaceAll('-', '')
  return `${compact}T235959Z`
}

const buildRruleFromState = () => {
  if (!ALLOWED_FREQUENCIES.includes(frequency.value)) {
    return ''
  }

  if (normalizedInterval.value === null) {
    return ''
  }

  const ruleParts = [
    `FREQ=${frequency.value}`,
    `INTERVAL=${normalizedInterval.value}`,
  ]

  if (frequency.value === 'WEEKLY') {
    const weeklyDays = selectedWeekdays.value.length > 0
      ? selectedWeekdays.value
      : [resolveDayCodeFromStartsAt()]

    ruleParts.push(`BYDAY=${weeklyDays.join(',')}`)
  }

  if (endType.value === 'count' && normalizedEndCount.value !== null) {
    ruleParts.push(`COUNT=${normalizedEndCount.value}`)
  }

  if (endType.value === 'until') {
    const until = formatUntilValue(endUntilDate.value)

    if (!until) {
      return ''
    }

    ruleParts.push(`UNTIL=${until}`)
  }

  return ruleParts.join(';')
}

const submitSchedule = async (event) => {
  event?.preventDefault?.()

  if (!canSubmit.value || typeof props.saveSchedule !== 'function') {
    return
  }

  const parsedStartsAt = new Date(startsAt.value)

  if (Number.isNaN(parsedStartsAt.getTime())) {
    toast.error('Please provide a valid start date and time.')
    return
  }

  if (rrule.value.trim() === '') {
    toast.error('Please provide a valid recurrence pattern.')
    return
  }

  try {
    await props.saveSchedule({
      starts_at: parsedStartsAt.toISOString(),
      rrule: rrule.value,
    })

    toast.success('Schedule saved.')
    closeModal()
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.schedule?.[0]
      || error?.response?.data?.errors?.['schedule.rrule']?.[0]
      || 'Unable to save schedule.'

    toast.error(errorMessage)
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>
